import {useCallback, useEffect, useState} from "react";
import {FileText} from "lucide-react";
import {createAdminCmsPage, deleteAdminCmsPage, listAdminCmsPages, updateAdminCmsPage, type AdminCmsPage} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminHtmlEditor, AdminLangTabs, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs} from "./ui";

const emptyForm = {numeEn: "", numeRo: "", continutEn: "", continutRo: ""};

export function CmsPagesView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("continut");
  const [items, setItems] = useState<AdminCmsPage[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminCmsPages({page, pageSize: 20, search: search || undefined}).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
    }).catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(p: AdminCmsPage) {
    setOpenId(p.id);
    setForm({numeEn: p.numeEn, numeRo: p.numeRo, continutEn: p.continutEn, continutRo: p.continutRo});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminCmsPage(form);
          adminToast.success("Page added.");
        } else if(typeof openId === "number") {
          await updateAdminCmsPage(openId, form);
          adminToast.success("Page saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete page?", async () => {
      await deleteAdminCmsPage(id);
      closeModal(false);
      load();
      adminToast.success("Page deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.continut.title}
      description="CMS pages and site content"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Content"}]}
      actions={<AdminAddButton onClick={openNew} label="Add page" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="continut" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(p) => p.id}
        getTitle={(p) => p.numeRo || p.numeEn}
        getSubtitle={(p) => (p.numeRo && p.numeEn ? p.numeEn : undefined)}
        getMeta={(p) => (
          <>
            {p.numeRo ? <AdminBadge variant="outline">RO</AdminBadge> : null}
            {p.numeEn ? <AdminBadge variant="outline">EN</AdminBadge> : null}
          </>
        )}
        getSearchText={(p) => adminSearchText(p.id, p.numeEn, p.numeRo, p.continutEn, p.continutRo)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No pages"
        emptyDescription="Add a new page or adjust filters."
        searchPlaceholder="Search page, name, content..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={true}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New page" : "Edit page"}
        icon={FileText}
        size="xl"
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminLangTabs
            ro={(
              <>
                <AdminField label="Name">
                  <Input value={form.numeRo} onChange={(e) => setForm({...form, numeRo: e.target.value})} />
                </AdminField>
                <AdminField label="Content">
                  <AdminHtmlEditor value={form.continutRo} onChange={(html) => setForm({...form, continutRo: html})} />
                </AdminField>
              </>
            )}
            en={(
              <>
                <AdminField label="Name">
                  <Input value={form.numeEn} onChange={(e) => setForm({...form, numeEn: e.target.value})} />
                </AdminField>
                <AdminField label="Content">
                  <AdminHtmlEditor value={form.continutEn} onChange={(html) => setForm({...form, continutEn: html})} />
                </AdminField>
              </>
            )}
          />
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
